import { parser, GFM } from "@lezer/markdown";
import { cn } from "@z/ui";
import { useState } from "react";
import { safeLink } from "./markdown";

const markdown = parser.configure(GFM);
export type MarkdownLink = {
  from: number;
  to: number;
  title: string;
  url: string;
  autolink?: boolean;
};
type Decoration = { from: number; to: number; name: string };
export type PreviewLine = {
  text: string;
  from: number;
  to: number;
  heading: number;
  code: boolean;
  quote: boolean;
  rule: boolean;
  image: { url: string; alt: string } | null;
  table: {
    header: boolean;
    delimiter: boolean;
    cells: Decoration[];
    columns: number;
    align: Array<"left" | "center" | "right">;
  } | null;
  decorations: Decoration[];
  links: MarkdownLink[];
};

/** Syntax offsets stay in the original source; never serialize a preview back into Markdown. */
export function parsePreview(text: string): PreviewLine[] {
  const decorations: Decoration[] = [],
    links: MarkdownLink[] = [];
  const images: Array<{ from: number; to: number; url: string; alt: string }> = [];
  const tables: Array<{
    from: number;
    to: number;
    columns: number;
    align: Array<"left" | "center" | "right">;
  }> = [];
  markdown.parse(text).iterate({
    enter(node) {
      decorations.push({ from: node.from, to: node.to, name: node.name });
      if (node.name === "Table") {
        const delimiter = node.node.getChild("TableDelimiter");
        const separators = delimiter
          ? text
              .slice(delimiter.from, delimiter.to)
              .trim()
              .replace(/^\||\|$/g, "")
              .split("|")
          : ["---"];
        const align = separators.map((cell): "left" | "center" | "right" =>
          /^\s*:.*:\s*$/.test(cell) ? "center" : /:\s*$/.test(cell) ? "right" : "left",
        );
        tables.push({ from: node.from, to: node.to, columns: align.length, align });
      }
      if (node.name === "Image") {
        const url = node.node.getChild("URL");
        const marks = node.node.getChildren("LinkMark");
        if (url && marks[0] && marks[1])
          images.push({
            from: node.from,
            to: node.to,
            url: text.slice(url.from, url.to).replace(/^<|>$/g, ""),
            alt: text.slice(marks[0].to, marks[1].from),
          });
      }
      if (node.name === "URL" && !["Link", "Image"].includes(node.node.parent?.name ?? "")) {
        const url = text.slice(node.from, node.to);
        if (safeLink(url))
          links.push({ from: node.from, to: node.to, title: url, url, autolink: true });
      }
      if (node.name === "Link") {
        const url = node.node.getChild("URL");
        const marks = node.node.getChildren("LinkMark");
        if (url && marks[0] && marks[1])
          links.push({
            from: node.from,
            to: node.to,
            title: text.slice(marks[0].to, marks[1].from).replace(/\\([\\[\]])/g, "$1"),
            url: text.slice(url.from, url.to).replace(/^<|>$/g, ""),
          });
      }
    },
  });
  let from = 0;
  return text.split("\n").map((line) => {
    const to = from + line.length;
    const ranges = decorations.filter((d) => d.from <= to && d.to > from);
    const heading = ranges.find((d) => /^ATXHeading/.test(d.name));
    const table = tables.find((t) => t.from <= from && t.to >= to);
    const image = images.find(
      (i) =>
        i.from >= from &&
        i.to <= to &&
        !text.slice(from, i.from).trim() &&
        !text.slice(i.to, to).trim(),
    );
    const result = {
      text: line,
      from,
      to,
      heading: heading ? Number(heading.name.slice(-1)) : 0,
      code: ranges.some((d) => d.name === "FencedCode" || d.name === "CodeBlock"),
      quote: ranges.some((d) => d.name === "Blockquote"),
      rule: ranges.some((d) => d.name === "HorizontalRule"),
      image:
        image && safeLink(image.url)?.startsWith("http")
          ? { url: safeLink(image.url)!, alt: image.alt }
          : null,
      table: table
        ? {
            header: ranges.some((d) => d.name === "TableHeader"),
            delimiter: ranges.some(
              (d) => d.name === "TableDelimiter" && d.from === from && d.to === to,
            ),
            cells: tableCells(line, from, ranges),
            columns: table.columns,
            align: table.align,
          }
        : null,
      decorations: ranges,
      links: links.filter((link) => link.from >= from && link.to <= to),
    };
    from = to + 1;
    return result;
  });
}

/** Formatting shared by the entire selection (or containing the insertion point). */
export function activeFormats(lines: PreviewLine[], from: number, to: number): ReadonlySet<string> {
  const active = new Set<string>();
  const selected = lines.filter(
    (line) => line.to >= from && (from === to ? line.from <= to : line.from < to),
  );
  if (!selected.length) return active;
  for (const [id, name] of [
    ["**", "StrongEmphasis"],
    ["*", "Emphasis"],
    ["`", "InlineCode"],
    ["link", "Link"],
  ]) {
    if (
      selected.every((line) =>
        line.decorations.some(
          (d) => d.name === name && d.from <= from && (from === to ? d.to > to : d.to >= to),
        ),
      )
    )
      active.add(id!);
  }
  for (const level of [1, 2, 3]) {
    if (selected.every((line) => line.heading === level)) active.add(`heading${level}`);
  }
  if (selected.every((line) => !line.code && /^\s*[-*+] (?!\[[ xX]\] )/.test(line.text)))
    active.add("list");
  if (selected.every((line) => !line.code && /^\s*[-*+] \[[ xX]\] /.test(line.text)))
    active.add("task");
  return active;
}

function tableCells(text: string, from: number, decorations: Decoration[]): Decoration[] {
  const pipes = decorations.filter(
    (d) => d.name === "TableDelimiter" && text.slice(d.from - from, d.to - from) === "|",
  );
  let start = from;
  const cells: Decoration[] = [];
  for (const pipe of pipes) {
    if (pipe.from !== from || text.slice(0, pipe.from - from).trim())
      cells.push({ from: start, to: pipe.from, name: "TableCell" });
    start = pipe.to;
  }
  if (text.slice(start - from).trim() || !pipes.length)
    cells.push({ from: start, to: from + text.length, name: "TableCell" });
  return cells;
}

function Syntax({ text, reveal }: { text: string; reveal: boolean }) {
  // Zero-size glyphs keep source Text nodes available for preview selections. Only the explicit source
  // toggle reveals them: changing geometry on focus breaks pointer placement and selection.
  return (
    <span
      data-markdown-syntax=""
      aria-hidden={!reveal}
      style={reveal ? undefined : { fontSize: 0 }}
      className="text-fg-faint"
    >
      {text}
    </span>
  );
}

function InlinePreview({
  line,
  start,
  reveal,
  onLink,
}: {
  line: PreviewLine;
  start: number;
  reveal: boolean;
  onLink: (link: MarkdownLink) => void;
}) {
  const ranges = line.decorations.filter((d) => d.from < line.to && d.to > start);
  const cuts = [
    ...new Set([
      start,
      line.to,
      ...ranges.flatMap((d) => [Math.max(start, d.from), Math.min(line.to, d.to)]),
    ]),
  ].sort((a, b) => a - b);
  return cuts.slice(0, -1).map((from, index) => {
    const to = cuts[index + 1]!;
    const source = line.text.slice(from - line.from, to - line.from);
    const names = ranges.filter((d) => d.from <= from && d.to >= to).map((d) => d.name);
    const link = line.links.find((l) => l.from <= from && l.to >= to);
    const syntax =
      names.some((name) => ["EmphasisMark", "CodeMark", "StrikethroughMark"].includes(name)) ||
      (link &&
        !link.autolink &&
        names.some((name) => ["LinkMark", "URL", "LinkTitle"].includes(name)));
    const content = syntax ? <Syntax text={source} reveal={reveal} /> : source;
    const className = cn(
      names.includes("StrongEmphasis") && "font-semibold",
      names.includes("Emphasis") && "italic",
      names.includes("Strikethrough") && "line-through text-fg-muted",
      names.includes("InlineCode") && "bg-surface-raised font-mono text-[0.9em] text-nav-purple",
      link && "text-accent underline decoration-accent/30 underline-offset-4",
    );
    return link && !syntax && safeLink(link.url) ? (
      <a
        key={from}
        href={safeLink(link.url)!}
        className={className}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          onLink(link);
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    ) : (
      <span key={from} className={className}>
        {content}
      </span>
    );
  });
}

function ImagePreview({ image }: { image: NonNullable<PreviewLine["image"]> }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <span
      contentEditable={false}
      role="img"
      aria-label={`Image unavailable: ${image.alt}`}
      data-caption={image.alt ? `Image unavailable · ${image.alt}` : "Image unavailable"}
      className="block rounded-lg border border-dashed border-border px-4 py-8 text-sm text-fg-muted after:content-[attr(data-caption)]"
    />
  ) : (
    <img
      contentEditable={false}
      src={image.url}
      alt={image.alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="my-2 block max-h-96 max-w-full rounded-lg object-contain"
    />
  );
}

function TablePreview({
  line,
  onLink,
}: {
  line: PreviewLine;
  onLink: (link: MarkdownLink) => void;
}) {
  const cells = line.table!.cells;
  return (
    <>
      {cells.map((cell, index) => {
        const position = cells[index - 1]?.to ?? line.from;
        const before = line.text.slice(position - line.from, cell.from - line.from);
        const clipped = {
          ...line,
          text: line.text.slice(cell.from - line.from, cell.to - line.from),
          from: cell.from,
          to: cell.to,
        };
        return (
          <span
            key={cell.from}
            data-table-cell=""
            style={{
              flex: `0 0 ${100 / Math.max(line.table!.columns, cells.length)}%`,
              textAlign: line.table!.align[index] ?? "left",
            }}
            className="min-w-0 border-r border-border px-3 py-2 last:border-r-0"
          >
            <Syntax text={before} reveal={false} />
            <InlinePreview line={clipped} start={cell.from} reveal={false} onLink={onLink} />
          </span>
        );
      })}
      {Array.from({ length: Math.max(0, line.table!.columns - cells.length) }, (_, index) => (
        <span
          key={`empty-${index}`}
          data-table-cell=""
          style={{ flex: `0 0 ${100 / line.table!.columns}%` }}
          className="min-w-0 border-r border-border px-3 py-2 last:border-r-0"
        />
      ))}
      <Syntax text={line.text.slice((cells.at(-1)?.to ?? line.from) - line.from)} reveal={false} />
    </>
  );
}

export function PreviewRow({
  line,
  reveal,
  onTask,
  onLink,
}: {
  line: PreviewLine;
  reveal: boolean;
  onTask: (position: number) => void;
  onLink: (link: MarkdownLink) => void;
}) {
  const list = !line.code && line.decorations.some((d) => d.name === "ListMark");
  const prefix = list ? /^([ \t]*)([-*+] |\d+[.)] )(\[[ xX]\] )?/.exec(line.text) : null;
  const heading = line.heading ? (/^(#{1,6} )/.exec(line.text)?.[0] ?? "") : "";
  const quote = !line.code && line.quote ? (/^[ \t]*> ?/.exec(line.text)?.[0] ?? "") : "";
  const hiddenPrefix = prefix?.[0] ?? (heading || quote);
  const task = prefix?.[3];
  const checked = task ? /\[[xX]\]/.test(task) : false;
  const showTaskControl = !reveal && !!task;
  return (
    <div
      data-markdown-line={line.from}
      data-heading={line.heading || undefined}
      data-source-visible={reveal}
      className={cn(
        "min-h-[1.75em]",
        // An empty document must share the placeholder's line box and typing baseline.
        // Only compact blank separators when there are other lines in the document.
        !line.text &&
          !line.code &&
          "[&:not(:only-child)]:min-h-[0.9em] [&:not(:only-child)]:leading-[0.9]",
        !reveal &&
          line.rule &&
          "relative my-4 after:absolute after:inset-x-0 after:top-1/2 after:border-t after:border-border",
        !reveal &&
          line.table &&
          !line.table.delimiter &&
          "flex border-x border-b border-border text-sm",
        !reveal && line.table?.header && "rounded-t-md border-t bg-surface font-medium",
        !reveal && line.table?.delimiter && "!min-h-0 h-0 overflow-hidden",
        line.heading > 0 && "pt-3 pb-1 font-semibold tracking-tight leading-snug",
        line.heading === 1
          ? "text-2xl"
          : line.heading === 2
            ? "text-xl"
            : line.heading > 2 && "text-lg",
        line.code && "bg-surface px-3 font-mono text-sm",
        line.quote && "border-l-2 border-accent/30 pl-3 text-fg-muted",
        checked && "text-fg-muted",
      )}
    >
      {hiddenPrefix && (
        <>
          {prefix?.[1]}
          {prefix ? (
            <span
              role={showTaskControl ? "checkbox" : undefined}
              aria-checked={showTaskControl ? checked : undefined}
              aria-label={
                showTaskControl
                  ? `Mark ${line.text.slice(hiddenPrefix.length)} ${checked ? "incomplete" : "complete"}`
                  : undefined
              }
              tabIndex={showTaskControl ? 0 : undefined}
              onMouseDown={showTaskControl ? (e) => e.preventDefault() : undefined}
              onClick={
                showTaskControl
                  ? (e) => {
                      e.preventDefault();
                      onTask(line.from);
                    }
                  : undefined
              }
              onKeyDown={
                showTaskControl
                  ? (e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        onTask(line.from);
                      }
                    }
                  : undefined
              }
              className={cn(
                showTaskControl &&
                  "relative mr-2 inline-flex size-3.5 cursor-pointer rounded-[3px] border align-middle leading-none focus-ring",
                showTaskControl &&
                  (checked
                    ? "border-accent bg-accent after:absolute after:left-[4px] after:top-[1px] after:h-[7px] after:w-[4px] after:rotate-45 after:border-b-[1.5px] after:border-r-[1.5px] after:border-bg"
                    : "border-fg-faint"),
                !reveal &&
                  !task &&
                  "relative mr-2 inline-block min-w-3 text-fg-faint after:content-[attr(data-bullet)]",
              )}
              data-bullet={
                !reveal && !task ? (/^\d/.test(prefix[2]!) ? prefix[2]!.trim() : "•") : undefined
              }
            >
              {/* Keep the marker's Text node mounted while Chrome owns the caret. */}
              <Syntax text={hiddenPrefix.slice(prefix[1]!.length)} reveal={reveal} />
            </span>
          ) : (
            <Syntax text={hiddenPrefix} reveal={reveal} />
          )}
        </>
      )}
      {!reveal && (line.rule || line.table?.delimiter) ? (
        <Syntax text={line.text} reveal={false} />
      ) : !reveal && line.image ? (
        <>
          <Syntax text={line.text} reveal={false} />
          <ImagePreview key={line.image.url} image={line.image} />
        </>
      ) : !reveal && line.table ? (
        <TablePreview line={line} onLink={onLink} />
      ) : line.text ? (
        line.code ? (
          line.text
        ) : (
          <InlinePreview
            line={line}
            start={line.from + hiddenPrefix.length}
            reveal={reveal}
            onLink={onLink}
          />
        )
      ) : (
        <br />
      )}
    </div>
  );
}
